/**
 * Module dependencies.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useRequestProvider } from 'app/components/providers/request-provider/request-provider';
import { resolveAppUrl } from 'app/core/utils/url-resolver';
import { apiEndpoints } from 'app/core/config/api-endpoints';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Button, Form, InputNumber, Modal, Select, Input, DatePicker } from 'antd';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import styled from 'styled-components';

/**
 * Styled components.
 */

const Container = styled.div`
  padding: 24px;
  max-width: 800px;
`;

const Section = styled.div`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h3`
  color: #005b96;
  margin-bottom: 16px;
  border-bottom: 1px solid #eee;
  padding-bottom: 8px;
`;

const Row = styled.div`
  display: flex;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
`;

const Label = styled.div`
  flex: 0 0 200px;
  font-weight: 500;
  color: #666;
`;

const Value = styled.div`
  flex: 1;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
`;

/**
 * `PharmacyPlan` component.
 */

export function PharmacyPlan(): JSX.Element {
  const { pharmacyId } = useParams<{ pharmacyId: string }>();
  const client = useRequestProvider();
  const [translate] = useTranslation();
  const [editOpen, setEditOpen] = useState(false);
  const [creditOpen, setCreditOpen] = useState(false);
  const [debitOpen, setDebitOpen] = useState(false);
  const [editForm] = Form.useForm();
  const [creditForm] = Form.useForm();
  const [debitForm] = Form.useForm();

  const { data: planSummary, refetch } = useQuery({
    queryKey: ['pharmacyPlan', pharmacyId],
    queryFn: async () => {
      const response = await client.get(
        resolveAppUrl(apiEndpoints.pharmacyPlan.plans, { interpolations: { pharmacyId } })
      );

      return response.data?.data;
    }
  });

  const { plan } = planSummary ?? {};

  const { data: planTypes } = useQuery({
    queryKey: ['pharmacyPlanTypes'],
    queryFn: async () => {
      const response = await client.get(resolveAppUrl(apiEndpoints.pharmacyPlan.planTypes));

      return response.data?.data;
    }
  });

  const editMutation = useMutation({
    mutationFn: (values: any) =>
      client.put(resolveAppUrl(apiEndpoints.pharmacyPlan.plans, { interpolations: { pharmacyId } }), {
        ...values,
        subscribedAt: values.subscribedAt?.toISOString() ?? null,
        expiresAt: values.expiresAt?.toISOString() ?? null
      }),
    onSuccess: () => {
      setEditOpen(false);
      refetch();
    }
  });

  const creditMutation = useMutation({
    mutationFn: (values: any) =>
      client.post(resolveAppUrl(apiEndpoints.pharmacyPlan.credit, { interpolations: { pharmacyId } }), values),
    onSuccess: () => {
      setCreditOpen(false);
      creditForm.resetFields();
      refetch();
    }
  });

  const debitMutation = useMutation({
    mutationFn: (values: any) =>
      client.post(resolveAppUrl(apiEndpoints.pharmacyPlan.debit, { interpolations: { pharmacyId } }), values),
    onSuccess: () => {
      setDebitOpen(false);
      debitForm.resetFields();
      refetch();
    }
  });

  const openEdit = useCallback(() => {
    const fallbackSubscribedAt = plan?.planType.hasSubscription ? moment().startOf('day') : null;
    const initialSubscribedAt = plan?.subscribedAt ? moment(plan.subscribedAt) : fallbackSubscribedAt;
    editForm.setFieldsValue({
      planTypeId: plan?.planTypeId,
      subscribedAt: initialSubscribedAt,
      expiresAt: plan?.expiresAt ? moment(plan.expiresAt) : null
    });
    setEditOpen(true);
  }, [editForm, plan?.expiresAt, plan?.planType.hasSubscription, plan?.planTypeId, plan?.subscribedAt]);

  const selectedPlanTypeId = Form.useWatch('planTypeId', editForm);
  const selectedPlanType = (planTypes ?? []).find((pt: any) => pt.id === selectedPlanTypeId);
  const planHasSubscription = selectedPlanType?.hasSubscription ?? false;

  useEffect(() => {
    const subscribedAt = editForm.getFieldValue('subscribedAt');
    if (planHasSubscription && subscribedAt === null) {
      editForm.setFieldValue('subscribedAt', moment().startOf('day'));
    } else if (!planHasSubscription) {
      editForm.setFieldValue('subscribedAt', null);
    }
  }, [planHasSubscription, editForm]);

  const planTypeOptions = (planTypes ?? []).map((pt: any) => ({
    value: pt.id,
    label: pt.name
  }));

  return (
    <Container>
      <Section>
        <SectionTitle>{translate('common.labels.pharmacyPlan')}</SectionTitle>

        <Row>
          <Label>{translate('common.labels.planType')}</Label>
          <Value>{plan?.planType?.name ?? '—'}</Value>
        </Row>
        <Row>
          <Label>{translate('common.labels.tokenBalance')}</Label>
          <Value>{plan?.tokenBalance ?? '—'}</Value>
        </Row>
        <Row>
          <Label>{translate('common.labels.subscribedAt')}</Label>
          <Value>{plan?.subscribedAt ? moment(plan.subscribedAt).format('YYYY-MM-DD') : '—'}</Value>
        </Row>
        <Row>
          <Label>{translate('common.labels.expiresAt')}</Label>
          <Value>{plan?.expiresAt ? moment(plan.expiresAt).format('YYYY-MM-DD') : '—'}</Value>
        </Row>
        <Row>
          <Label>{translate('common.labels.cancelledAt')}</Label>
          <Value>{plan?.cancelledAt ? moment(plan.cancelledAt).format('YYYY-MM-DD') : '—'}</Value>
        </Row>

        <Actions>
          <Button onClick={openEdit}>{translate('common.actions.edit')}</Button>
          <Button onClick={() => setCreditOpen(true)} type={'primary'}>
            {translate('common.actions.credit')}
          </Button>
          <Button danger onClick={() => setDebitOpen(true)}>
            {translate('common.actions.debit')}
          </Button>
        </Actions>
      </Section>

      <Modal
        confirmLoading={editMutation.isLoading}
        onCancel={() => setEditOpen(false)}
        onOk={() => editForm.submit()}
        open={editOpen}
        title={translate('common.labels.editPlan')}
      >
        <Form form={editForm} layout={'vertical'} onFinish={values => editMutation.mutate(values)}>
          <Form.Item label={translate('common.labels.planType')} name={'planTypeId'}>
            <Select options={planTypeOptions} />
          </Form.Item>
          <Form.Item
            label={translate('common.labels.subscribedAt')}
            name={'subscribedAt'}
            rules={[{ required: planHasSubscription }]}
          >
            <DatePicker disabled={!planHasSubscription} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label={translate('common.labels.expiresAt')} name={'expiresAt'}>
            <DatePicker disabled={!planHasSubscription} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        confirmLoading={creditMutation.isLoading}
        onCancel={() => setCreditOpen(false)}
        onOk={() => creditForm.submit()}
        open={creditOpen}
        title={translate('common.actions.credit')}
      >
        <Form form={creditForm} layout={'vertical'} onFinish={values => creditMutation.mutate(values)}>
          <Form.Item label={translate('common.labels.amount')} name={'amount'} rules={[{ required: true }]}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label={translate('common.labels.reason')} name={'reason'} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        confirmLoading={debitMutation.isLoading}
        onCancel={() => setDebitOpen(false)}
        onOk={() => debitForm.submit()}
        open={debitOpen}
        title={translate('common.actions.debit')}
      >
        <Form form={debitForm} layout={'vertical'} onFinish={values => debitMutation.mutate(values)}>
          <Form.Item label={translate('common.labels.amount')} name={'amount'} rules={[{ required: true }]}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label={translate('common.labels.reason')} name={'reason'} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </Container>
  );
}
